// frontend/src/pages/shop/ShopSettings.jsx
import { useState, useEffect } from 'react';
import { shopBankAccountAPI } from '../../services/api';
import ShopLayout from '../../components/layout/ShopLayout';

const ShopSettings = () => {
  const [activeTab, setActiveTab] = useState('bank-accounts');
  
  // Bank Accounts State
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    bank_name: '',
    account_number: '',
    account_name: '',
    iban_number: '',
    is_default: false
  });
  const [saving, setSaving] = useState(false);

  // Fetch accounts
  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await shopBankAccountAPI.getAll();
      if (response.data.success) {
        setAccounts(response.data.data);
      }
    } catch (error) {
      console.error('Fetch accounts error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'bank-accounts') {
      fetchAccounts();
    }
  }, [activeTab]);

  // Open modal for create
  const handleCreate = () => {
    setEditingAccount(null);
    setFormData({
      bank_name: '',
      account_number: '',
      account_name: '',
      iban_number: '',
      is_default: false
    });
    setShowModal(true);
  };

  // Open modal for edit
  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      bank_name: account.bank_name,
      account_number: account.account_number,
      account_name: account.account_name,
      iban_number: account.iban_number || '',
      is_default: account.is_default
    });
    setShowModal(true);
  };

  // Save account
  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!formData.bank_name || !formData.account_number || !formData.account_name) {
      alert('Банкны нэр, дансны дугаар, эзэмшигчийн нэр оруулна уу');
      return;
    }

    setSaving(true);
    try {
      if (editingAccount) {
        await shopBankAccountAPI.update(editingAccount.id, formData);
        alert('Данс амжилттай шинэчлэгдлээ');
      } else {
        await shopBankAccountAPI.create(formData);
        alert('Данс амжилттай нэмэгдлээ');
      }
      setShowModal(false);
      fetchAccounts();
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  // Delete account
  const handleDelete = async (id) => {
    if (!confirm('Энэ дансыг устгахдаа итгэлтэй байна уу?')) return;
    
    try {
      await shopBankAccountAPI.delete(id);
      alert('Данс устгагдлаа');
      fetchAccounts();
    } catch (error) {
      alert(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  // Set default account
  const handleSetDefault = async (id) => {
    try {
      await shopBankAccountAPI.setDefault(id);
      fetchAccounts();
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    }
  };

  return (
    <ShopLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Тохиргоо</h1>
          <p className="text-gray-500 mt-1">Шопын тохиргоо болон дансны удирдлага</p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-1">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('bank-accounts')}
              className={`flex-1 sm:flex-none px-5 py-2.5 rounded-lg font-medium transition-all text-sm ${
                activeTab === 'bank-accounts'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <span>🏦</span>
                <span>Дансны тохиргоо</span>
              </span>
            </button>
            <button
              onClick={() => setActiveTab('general')}
              className={`flex-1 sm:flex-none px-5 py-2.5 rounded-lg font-medium transition-all text-sm ${
                activeTab === 'general'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <span>⚙️</span>
                <span>Ерөнхий</span>
              </span>
            </button>
          </div>
        </div>

        {/* Bank Accounts Tab */}
        {activeTab === 'bank-accounts' && (
          <div className="space-y-4">
            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <span className="text-xl">💡</span>
                <div>
                  <p className="font-medium text-blue-800">Барааны төлбөр хүлээн авах данс</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Хүргэлтийн компани таны хүргэлтийн барааны төлбөрийг энэ данс руу шилжүүлнэ.
                  </p>
                </div>
              </div>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Миний данснууд</h2>
                <p className="text-sm text-gray-500">{accounts.length} данс бүртгэгдсэн</p>
              </div>
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition flex items-center gap-2"
              >
                <span>➕</span>
                <span>Данс нэмэх</span>
              </button>
            </div>

            {/* Accounts List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
                </div>
              ) : accounts.length === 0 ? (
                <div className="text-center py-12">
                  <span className="text-5xl">🏦</span>
                  <p className="mt-4 text-gray-500 font-medium">Данс бүртгэгдээгүй байна</p>
                  <p className="text-sm text-gray-400 mt-1">COD төлбөр хүлээн авах дансаа нэмнэ үү</p>
                  <button
                    onClick={handleCreate}
                    className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
                  >
                    Данс нэмэх
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {accounts.map((account) => (
                    <div key={account.id} className="p-4 hover:bg-gray-50 transition">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                            <span className="text-2xl">🏦</span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-gray-900">{account.bank_name}</p>
                              {account.is_default && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                  Үндсэн
                                </span>
                              )}
                              {!account.is_active && (
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                                  Идэвхгүй
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 font-mono">{account.account_number}</p>
                            <p className="text-sm text-gray-500">{account.account_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!account.is_default && (
                            <button
                              onClick={() => handleSetDefault(account.id)}
                              className="px-3 py-1.5 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition"
                              title="Үндсэн болгох"
                            >
                              ⭐
                            </button>
                          )}
                          <button
                            onClick={() => handleEdit(account)}
                            className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition"
                          >
                            ✏️ Засах
                          </button>
                          <button
                            onClick={() => handleDelete(account.id)}
                            className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition"
                          >
                            🗑️ Устгах
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* General Settings Tab */}
        {activeTab === 'general' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="text-center py-8">
              <span className="text-5xl">🚧</span>
              <p className="mt-4 text-gray-500">Ерөнхий тохиргоо удахгүй нэмэгдэнэ</p>
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-t-xl">
                <h2 className="text-lg font-bold text-gray-900">
                  {editingAccount ? '🏦 Данс засах' : '🏦 Шинэ данс нэмэх'}
                </h2>
              </div>
              <form onSubmit={handleSave} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Банкны нэр *</label>
                  <select
                    value={formData.bank_name}
                    onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="">Сонгоно уу</option>
                    <option value="Хаан банк">Хаан банк</option>
                    <option value="Голомт банк">Голомт банк</option>
                    <option value="Худалдаа хөгжлийн банк">Худалдаа хөгжлийн банк</option>
                    <option value="Хас банк">Хас банк</option>
                    <option value="Төрийн банк">Төрийн банк</option>
                    <option value="Капитрон банк">Капитрон банк</option>
                    <option value="Богд банк">Богд банк</option>
                    <option value="М банк">М банк</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Дансны дугаар *</label>
                  <input
                    type="text"
                    value={formData.account_number}
                    onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                    placeholder="1234567890"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Данс эзэмшигч *</label>
                  <input
                    type="text"
                    value={formData.account_name}
                    onChange={(e) => setFormData({...formData, account_name: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                    placeholder="Овог Нэр"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IBAN (заавал биш)</label>
                  <input
                    type="text"
                    value={formData.iban_number}
                    onChange={(e) => setFormData({...formData, iban_number: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                    placeholder="MN..."
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="is_default" className="text-sm text-gray-700">Үндсэн данс болгох</label>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                  >
                    Болих
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50"
                  >
                    {saving ? 'Хадгалж байна...' : 'Хадгалах'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </ShopLayout>
  );
};

export default ShopSettings;